/**
 * ============================================================
 * BELONGING SESSIONS SSO HANDLER FOR M.AI.RY - UPDATED
 * Using federated-auth endpoint for validation
 * ============================================================
 */

const BELONGING_SESSIONS_API = process.env.REACT_APP_BELONGING_API || 'https://futuremind-chat.preview.emergentagent.com/api';
const WEBHOOK_KEY = process.env.REACT_APP_WEBHOOK_KEY || 'bWAO73qH1emTy0CoT3rdwOsk5vy5awlu';

/**
 * Handle incoming SSO from Belonging Sessions
 * Uses new validation endpoint
 */
export async function handleBelongingSessionsSSO() {
  const params = new URLSearchParams(window.location.search);
  const bs_user = params.get('bs_user');
  const bs_token = params.get('bs_token');
  const bs_time = params.get('bs_time');
  
  if (!bs_user || !bs_token || !bs_time) {
    return null; // No SSO parameters
  }
  
  try {
    // Validate with Belonging Sessions federated auth
    const response = await fetch(`${BELONGING_SESSIONS_API}/sso/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Source-App': 'mairy'
      },
      body: JSON.stringify({ bs_user, bs_token, bs_time })
    });
    
    if (!response.ok) {
      console.error('❌ SSO validation failed');
      return null;
    }
    
    const data = await response.json();
    
    if (!data.valid) {
      console.error('❌ Invalid SSO token');
      return null;
    }
    
    // Valid SSO! Store user
    const user = {
      id: data.user.id,
      name: data.user.name,
      email: data.user.email,
      roles: data.user.roles || [],
      source: 'belonging_sessions',
      authenticated_at: new Date().toISOString()
    };
    
    localStorage.setItem('mairy_user', JSON.stringify(user));
    
    // Clean URL (remove SSO parameters)
    window.history.replaceState({}, '', window.location.pathname);
    
    console.log('✅ Belonging Sessions SSO successful:', user.name);
    return user;
  } catch (error) {
    console.error('❌ SSO error:', error);
    return null;
  }
}

/**
 * Get current authenticated user
 */
export function getBelongingUser() {
  try {
    const userStr = localStorage.getItem('mairy_user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    return null;
  }
}

/**
 * Check if user is authenticated via Belonging Sessions
 */
export function isAuthenticatedViaBelonging() {
  const user = getBelongingUser();
  return user && user.source === 'belonging_sessions';
}

/**
 * Sync wisdom conversation to Belonging Sessions dashboard
 * Uses webhook endpoint
 */
export async function syncConversationToBelongingSessions(conversation) {
  const user = getBelongingUser();
  
  if (!user || !user.id) {
    console.log('No Belonging Sessions user to sync to');
    return false;
  }
  
  try {
    const response = await fetch(`${BELONGING_SESSIONS_API}/webhooks/mairy/conversation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Webhook-Key': WEBHOOK_KEY
      },
      body: JSON.stringify({
        user_id: user.id,
        session_id: conversation.id || `session_${Date.now()}`,
        conversation_type: 'wisdom_chat',
        messages: conversation.messages || conversation.questions?.map(q => ({ role: 'user', content: q })) || [],
        summary: conversation.title || 'M.ai.ry conversation'
      })
    });
    
    if (response.ok) {
      console.log('✅ Conversation synced to Belonging Sessions dashboard');
      return true;
    } else {
      console.error('Failed to sync:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Failed to sync to Belonging Sessions:', error);
    return false;
  }
}

/**
 * Sync insight to user's dashboard
 */
export async function syncInsightToDashboard(userId, insightContent, insightType = 'wisdom') {
  try {
    await fetch(`${BELONGING_SESSIONS_API}/webhooks/mairy/insight`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Webhook-Key': WEBHOOK_KEY
      },
      body: JSON.stringify({
        user_id: userId,
        insight_type: insightType,
        content: insightContent,
        context: 'Generated from m.ai.ry conversation'
      })
    });
    
    console.log('✅ Insight synced to dashboard');
    return true;
  } catch (error) {
    console.error('Failed to sync insight:', error);
    return false;
  }
}

/**
 * Return to Belonging Sessions with SSO
 * Call this when user clicks "Return to Belonging Sessions" button
 */
export async function returnToBelongingSessions() {
  const user = getBelongingUser();
  
  if (!user || !user.id) {
    console.error('Unable to return to Belonging Sessions. No user found.');
    return false;
  }
  
  try {
    // Request return token
    const response = await fetch(
      `${BELONGING_SESSIONS_API}/auth/create-cross-site-token`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user.id
        },
        body: JSON.stringify({ return_url: '/dashboard' })
      }
    );
    
    const data = await response.json();
    
    if (data.redirect_url) {
      // Redirect back
      window.location.href = data.redirect_url;
      return true;
    } else {
      // Fallback: open Belonging Sessions normally
      window.open(BELONGING_API || 'https://belongingsessions.com', '_blank');
      return false;
    }
    
  } catch (error) {
    console.error('Failed to return:', error);
    // Fallback: open Belonging Sessions normally
    window.open(BELONGING_API || 'https://belongingsessions.com', '_blank');
    return false;
  }
}

/**
 * Show notification helper
 */
export function showNotification(message, type = 'success') {
  const div = document.createElement('div');
  div.textContent = message;
  
  const colors = {
    success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    info: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
    warning: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
  };
  
  div.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: ${colors[type] || colors.success};
    color: white;
    padding: 16px 24px;
    border-radius: 12px;
    z-index: 10000;
    font-family: 'JetBrains Mono', monospace;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
    font-weight: 500;
    animation: slideIn 0.3s ease-out;
  `;
  
  // Add animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideIn {
      from {
        transform: translateX(400px);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
  `;
  document.head.appendChild(style);
  
  document.body.appendChild(div);
  setTimeout(() => {
    div.style.animation = 'slideIn 0.3s ease-out reverse';
    setTimeout(() => div.remove(), 300);
  }, 4000);
}

/**
 * Logout from Belonging Sessions
 */
export function logoutBelongingSessions() {
  localStorage.removeItem('mairy_user');
  showNotification('Logged out successfully', 'info');
}
